import React from 'react'
import { useHotkeys } from 'react-hotkeys-hook'
import styled, { css } from 'styled-components'

import { Flex } from '../Flex'
import { Icon, type IconProps } from '../Icon'
import { Typography } from '../Typography'
import useDebouncedCallback from '../hooks/useDebouncedCallback'
import { Modal } from '../Modal'
import FormInput from '../FormInput/FormInput'
import { Loading } from '../Loading'
import { getArchbeeDocs } from './utils'

export enum OmniSearchTargetType {
  INTERNAL_LINK = 'INTERNAL_LINK',
  HELP_DOC = 'HELP_DOC',
}

type OmniSearchOption_InternalLink = {
  type: OmniSearchTargetType.INTERNAL_LINK
  breadcrumbs?: string[]
  route: string
  scrollId?: string
}

type OmniSearchOption_HelpDoc = {
  type: OmniSearchTargetType.HELP_DOC
  description: string
  link: string
}

export type OmniSearchOption = {
  key: string //unique key
  label: string
  keywords?: string[] //additional keywords to search on
  type: OmniSearchTargetType
} & (OmniSearchOption_InternalLink | OmniSearchOption_HelpDoc)

const ModalHeader = styled.div`
    border-bottom: 1px solid var(--border);
    padding-bottom: 10px;
    margin-bottom: 20px;
    display: flex;
    flex-direction: column;
    gap: 10px;
`

const ModalBody = styled.div`
    display: flex;
    flex-direction: column;
    gap: 40px;
`

const ModalSection = styled.section`
    display: flex;
    flex-direction: column;
    gap: 20px;

    .omni-search-result {
        &:last-child {
            border-bottom: none;
        }
    }
`

const SearchResult = ({
  result,
  onClick,
  highlight,
}: {
  result: OmniSearchOption
  onClick: () => void
  highlight?: boolean
}) => {
  return (
    <Flex
      data-testid={`omni-search-result-${result.key}`}
      className={
        highlight
          ? 'omni-search-result omni-search-result-highlight'
          : 'omni-search-result'
      }
      direction='row'
      gap='10px'
      align='center'
      css={css`
            padding: 15px;
            background-color: var(--surface-primary);
            cursor: pointer;
            border-bottom: 1px solid var(--border);

            &:last-child {
                border-bottom: none;
            }

            ${
              highlight &&
              `
                  background: var(--surface-secondary);

                  .omni-result-label {
                    color: var(--brand-primary);
                    font-weight: 600;
                  }
                `
            }
        `}
      onClick={onClick}
    >
      <Flex
        direction='column'
        align='center'
        justify='center'
        style={{
          background: 'var(--surface-tertiary)',
          borderRadius: '50%',
          color: 'var(--brand-primary)',
        }}
      >
        <Icon
          type={
            result.type === OmniSearchTargetType.INTERNAL_LINK
              ? 'QUESTION_MARK_CIRCLED'
              : 'EXTERNAL_LINK'
          }
          size='medium'
        />
      </Flex>
      <Flex direction='column' gap='5px'>
        <Typography className='omni-result-label' variant='body-sm'>
          {result.label}
        </Typography>
        {result.type === OmniSearchTargetType.INTERNAL_LINK && (
          <Typography variant='body-xs'>
            {result.breadcrumbs?.join(' > ')}
          </Typography>
        )}
        {result.type === OmniSearchTargetType.HELP_DOC && (
          <DescriptionText variant='body-xs'>
            {/* biome-ignore lint/security/noDangerouslySetInnerHtml: Used for highlighting */}
            <div dangerouslySetInnerHTML={{ __html: result.description }} />
          </DescriptionText>
        )}
      </Flex>
    </Flex>
  )
}

const DescriptionText = styled(Typography)`
  .span-search {
    background-color: var(--yellow200);
    font-weight: 600;
  }
`

const KeyIcon = ({
  iconType,
  fallbackText,
}: {
  iconType?: IconProps['type']
  fallbackText?: string
}) => {
  return (
    <Flex
      css={css`
    background: var(--surface-tertiary);
    padding: 4px;
    border-radius: 4px;
    height: 18px;
    width: ${iconType ? '17px' : 'auto'};
  `}
      direction='column'
      align='center'
      justify='center'
    >
      {iconType && <Icon type={iconType} height={16} width={16} />}
      {fallbackText && !iconType && (
        <Typography
          variant='body-xs'
          style={{ fontWeight: 600, color: 'var(--text-secondary)' }}
        >
          {fallbackText}
        </Typography>
      )}
    </Flex>
  )
}

export default function OmniSearch({
  searchOptions,
  onLinkClick,
  onClose,
  onSearchChange,
  enabled, //defaults to true
  open,
  enableDocsSearch,
  ...props
}: {
  loading?: boolean // Dynamically update loading state (for async search's)
  searchOptions: OmniSearchOption[]
  onLinkClick: (
    result: OmniSearchOption & {
      type: OmniSearchTargetType.INTERNAL_LINK
    }
  ) => void
  onSearchChange?: (search: string) => void
  onClose?: () => void
  enabled?: boolean
  open?: boolean
  enableDocsSearch?: boolean
}) {
  enabled = enabled ?? true

  const [isLoading, setIsLoading] = React.useState(props.loading)
  const [isOpen, setIsOpen] = React.useState(open && enabled)
  const [search, _setSearch] = React.useState('')
  const [highlightedIndex, setHighlightedIndex] = React.useState<number>(0)
  const isMac = /mac/i.test(navigator.userAgent)

  const setSearch = (val: string) => {
    if (val) setIsLoading(true)
    _setSearch(val)
    onSearchChange?.(val)
  }

  useHotkeys(isMac ? 'meta+k' : 'ctrl+k', (e) => {
    e.preventDefault()
    e.stopPropagation()

    if (!enabled || isOpen) return

    setIsOpen(true)
  })

  const closeModal = () => {
    setSearch('')
    setIsOpen(false)
    setIsLoading(false)
    onClose?.()
  }

  const handleChange = (result: OmniSearchOption) => {
    closeModal()

    if (result.type === OmniSearchTargetType.INTERNAL_LINK) {
      onLinkClick(result)
    } else if (result.type === OmniSearchTargetType.HELP_DOC) {
      window.open(result.link, '_blank')
    }
  }

  const [searchResults, setSearchResults] = React.useState<OmniSearchOption[]>(
    []
  )

  const changeSearchCB = React.useCallback(
    async (searchValue: string) => {
      setHighlightedIndex(0)
      setIsLoading(true)

      const extraOptions = enableDocsSearch
        ? await getArchbeeDocs(searchValue)
        : []

      setSearchResults([
        ...filterSearchResults(searchOptions, searchValue),
        ...extraOptions,
      ])

      setIsLoading(false)
    },
    [searchOptions, enableDocsSearch]
  )

  const { callback: handleSearchChanged, cancel: cancelSearch } =
    useDebouncedCallback(changeSearchCB, 350)

  React.useEffect(() => {
    if (search.length > 0) {
      cancelSearch()
      setSearchResults(filterSearchResults(searchOptions, search))
      setIsLoading(false)
    }
  }, [searchOptions])

  React.useEffect(() => {
    setIsOpen(open && enabled)
  }, [open, enabled])

  const inAppSearchResults = React.useMemo(
    () =>
      searchResults
        .filter((s) => s.type === OmniSearchTargetType.INTERNAL_LINK)
        .slice(0, 5),
    [searchResults]
  )
  const helpDocSearchResults = React.useMemo(
    () =>
      searchResults
        .filter((s) => s.type === OmniSearchTargetType.HELP_DOC)
        .slice(0, 10),
    [searchResults]
  )
  const allResults = React.useMemo(() => {
    return [...inAppSearchResults, ...helpDocSearchResults]
  }, [inAppSearchResults, helpDocSearchResults])

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'Enter' || e.key === 'ArrowDown') {
        setIsOpen(true)
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightedIndex((prev) => {
          return prev < allResults.length - 1 ? prev + 1 : 0
        })
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightedIndex((prev) => {
          return prev > 0 ? prev - 1 : allResults.length - 1
        })
        break
      case 'Enter':
        e.preventDefault()

        const firstOption = allResults?.[0]

        if (allResults.length === 1) {
          handleChange(firstOption)
          break
        }

        if (highlightedIndex >= 0 && highlightedIndex < allResults.length) {
          const option = allResults[highlightedIndex]

          handleChange(option)
        }

        break
      case 'Escape':
        closeModal()
        break
    }
  }

  const loading = props.loading || isLoading
  const hasSearchResults = searchResults.length > 0
  const showResults = !loading && hasSearchResults

  return isOpen ? (
    <Modal
      closeBtn={true}
      open={isOpen}
      onClose={() => closeModal()}
      containerSyle={{
        borderRadius: 'var(--border-radius-lg)',
        marginBottom: '100px',
      }}
    >
      <div onKeyDown={handleKeyDown}>
        <ModalHeader>
          <FormInput
            autoFocus={true}
            value={search}
            onChange={({ target: { value } }) => {
              setSearch(value)

              if (!value) {
                cancelSearch()
                setSearchResults([])
                setIsLoading(false)
                return
              }

              handleSearchChanged(value)
            }}
            leftAdornment={<Icon type='SEARCH' />}
            style={{
              border: 'none',
            }}
            baseOverrides={{
              borderColor: 'transparent',
              brandColor: 'transparent',
            }}
            onFocus={(e) => {
              if (search?.length) {
                e.target.select()
              }
            }}
          />
          <Flex
            css={css`
            display: flex;
            flex-direction: row;
            `}
            justify='space-between'
          >
            <Flex direction='row' align='center' gap='5px'>
              <Typography variant='body-xs'>Use</Typography>
              <KeyIcon
                iconType={isMac ? 'KEY_CMD' : undefined}
                fallbackText={!isMac ? 'CTRL' : 'CMD'}
              />
              <KeyIcon iconType='KEY_K' />
              <Typography variant='body-xs'>to open search bar</Typography>
            </Flex>
            <Flex direction='row' gap='5px' align='center'>
              <Typography variant='body-xs'>Use arrow keys</Typography>
              <KeyIcon iconType='ARROW_UP' />
              <KeyIcon iconType='ARROW_DOWN' />
              <Typography variant='body-xs'>to navigate</Typography>
            </Flex>
          </Flex>
        </ModalHeader>
        <ModalBody
          onKeyDown={handleKeyDown}
          style={{
            maxHeight: '500px',
            overflowY: 'auto',
            scrollbarWidth: 'thin',
          }}
        >
          {!showResults && (
            <Flex
              direction='column'
              align='center'
              style={{
                padding: '100px 0',
              }}
            >
              {loading && <Loading />}
              {!loading &&
                (search?.length ? (
                  <>
                    <Typography variant='h3'>No results found</Typography>
                    <Typography variant='body-xs'>
                      Try searching for something else
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant='h3'>
                      Search in-app or help docs
                    </Typography>
                  </>
                ))}
            </Flex>
          )}
          {showResults && inAppSearchResults.length > 0 && (
            <ModalSection data-testid='omni-search-results-in-app'>
              <Typography variant='h4'>In App</Typography>
              <div
                css={css`
        display: flex;
        flex-direction: column;
        `}
              >
                {inAppSearchResults.map((s) => {
                  return (
                    <SearchResult
                      key={s.key}
                      result={s}
                      onClick={() => handleChange(s)}
                      highlight={highlightedIndex === allResults.indexOf(s)}
                    />
                  )
                })}
              </div>
            </ModalSection>
          )}
          {showResults && helpDocSearchResults.length > 0 && (
            <ModalSection data-testid='omni-search-results-help-docs'>
              <Typography variant='h4'>Help Docs</Typography>
              <div
                css={css`
          display: flex;
          flex-direction: column;
          `}
              >
                {helpDocSearchResults.map((s) => {
                  return (
                    <SearchResult
                      key={s.key}
                      result={s}
                      onClick={() => handleChange(s)}
                      highlight={highlightedIndex === allResults.indexOf(s)}
                    />
                  )
                })}
              </div>
            </ModalSection>
          )}
        </ModalBody>
      </div>
    </Modal>
  ) : null
}

export const filterSearchResults = (
  searchOptions: OmniSearchOption[],
  val: string
) => {
  const searchVal = new RegExp(`\\*?${val.replace(/ /g, '\\s?')}\\*?`, 'gi')

  return searchOptions.filter((s) => {
    const label = s.label
    const keywords = s.keywords ?? []
    const description = (s as OmniSearchOption_HelpDoc).description ?? ''
    return (
      label.match(searchVal) ||
      description.match(searchVal) ||
      keywords.some((k) => k.match(searchVal))
    )
  })
}
